import * as S from './styled';

interface StarProps {
  size: number;
  color?: string;
  score: number;
  onClick?: () => void;
}

interface RatingProps {
  rating: number;
  size: number;
  color?: string;
  readOnly?: boolean;
  onClick?: (value: number) => void;
  gap?: number;
  ratingInfo?: {
    avgRating: number;
    ratingCount?: number;
    fontSize?: number;
    onClick?: () => void;
  };
}

/**
 * 사용자 평가 수집 및 노출을 위한 개별 Star을 나타내는 컴포넌트입니다.
 * @param {StarProps} props
 * @returns JSX Element
 */
function Star({ size, color, score, onClick }: StarProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 13 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      onClick={onClick}
    >
      <defs>
        <linearGradient id={`starGradient-${score}`}>
          <stop offset={`${score}%`} stopColor={color} />
          <stop offset={`${score}%`} stopColor="var(--color-ui-grayscale-gray-200)" />
        </linearGradient>
      </defs>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.9638 3.38625L6.50001 0.75L5.03622 3.38625C4.93281 3.5789 4.78313 3.74587 4.59922 3.87375C4.41441 4.00228 4.20056 4.0883 3.97459 4.125L0.958344 4.68525L3.06418 7.071C3.35216 7.37001 3.48805 7.77321 3.43626 8.175L3.03568 11.25L5.81284 9.975C6.02708 9.8767 6.26196 9.82543 6.50001 9.825C6.72335 9.82564 6.94327 9.87709 7.14126 9.975L9.98334 11.1713L9.58276 8.133C9.53213 7.73209 9.66825 7.33017 9.95564 7.032L12.0417 4.68525L9.02543 4.125C8.79918 4.08841 8.58505 4.00239 8.40001 3.87375C8.21639 3.74578 8.06699 3.57882 7.9638 3.38625Z"
        fill={`url(#starGradient-${score})`}
      />
    </svg>
  );
}

/**
 * 사용자 평가에 대한 정보를 나타내고 입력받는 Rating 컴포넌트입니다.
 * @param {RatingProps} props
 * @returns JSX Element
 */
export function Rating({
  rating,
  size,
  color = '#ffc633',
  onClick,
  readOnly,
  gap,
  ...props
}: RatingProps) {
  const MAX_RATING = 5;

  /** generateStarScores 함수는 주어진 값(value)을 기반으로 별점 배열을 생성합니다.
   * 함수는 MAX_RATING 상수에 정의된 최대 별점 수만큼의 길이를 갖는 배열을 생성하고, 각 별점에 대한 점수를 계산하여 배열에 담습니다.
   * 계산은 calculateStarScore 함수를 사용합니다.
   * @param {number} value - 현재 평가 값
   * @returns {number[]} - 별점 배열
   */
  const generateStarScores = (value: number): number[] => {
    const scoreArray = Array.from({ length: MAX_RATING }, (_, index) =>
      calculateStarScore(value, index),
    );

    return scoreArray;
  };

  /** calculateStarScore 함수는 주어진 값(value)과 현재 별점의 인덱스(index)를 기반으로 해당 별점에 대한 점수를 계산합니다.
   * 별점들은 주어진 값과 인덱스를 이용하여 계산된 multiplier 값을 기반으로 최종 점수를 계산합니다.
   * @param {number} value - 현재 평가 값
   * @param {number} index - 현재 별점의 인덱스
   * @returns {number} - 계산된 별점 점수
   */
  const calculateStarScore = (value: number, index: number): number => {
    const BASE_SCORE = 100;

    const multiplier = Math.max(0, Math.min(1, value - index));
    return Math.round(BASE_SCORE * multiplier);
  };

  return (
    <S.RatingContainer gap={gap}>
      {[...Array(MAX_RATING)].map((_, i) => {
        const ratingValue = i + 1;
        return (
          <S.StarWrapper
            key={i}
            readonly={readOnly ?? false}
            onClick={() => onClick && !readOnly && onClick(ratingValue)}
          >
            <Star size={size} score={generateStarScores(rating)[i]} color={color} />
          </S.StarWrapper>
        );
      })}
      {props.ratingInfo && (
        <S.RatingInfo fontSize={size * 0.8}>
          <p>{props.ratingInfo.avgRating}</p>
          {props.ratingInfo?.ratingCount !== undefined && (
            <p className="text-bold text-underline review-cnt" onClick={props.ratingInfo.onClick}>
              리뷰 {props.ratingInfo.ratingCount}개
            </p>
          )}
        </S.RatingInfo>
      )}
    </S.RatingContainer>
  );
}
